import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { buildCallViewerLink } from '../../shared/utils/call-viewer-link.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { catchError, EMPTY, filter, forkJoin, map, Observable, Subject, switchMap, take, of } from 'rxjs';
import { ToastService } from '../../ui';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { SNACKBAR_ERROR_CONFIG } from '../../shared/utils/notification-defaults';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { SessionService } from '../../core/session/session.service';
import { AuthService } from '../../core/auth/auth.service';
import { PhoneServiceApiService } from '../../core/services/phone-service-api.service';
import {
  CallGroup,
  CliSignalledNumber,
  Language,
  PhoneServiceDetail,
  Skill,
} from '../../core/services/phone-service-api.model';
import { UserAccountApiService } from '../../core/services/user-account-api.service';
import { UserAccountDetail } from '../../core/services/user-account-api.model';
import { LoggingService } from '../../core/services/logging.service';
import { safeHttpMessage } from '../../core/error/safe-http-message';
import { CcNumbersService } from '../cc-numbers/cc-numbers.service';
import { PhoneNumber } from '../cc-numbers/cc-numbers.model';
import { ContractsService } from '../contracts/contracts.service';
import { todayIso } from '../../shared/utils/iso-date.util';
import { getCurrency } from '../../shared/utils/currency.util';
import { SipOrderItem, SipOrderLinkSectionComponent } from './sip-order-link/sip-order-link-section.component';
import { HelpIconComponent } from '../../shared/help-icon/help-icon.component';
import {
  deriveOutboundReadiness,
  numberStateBadge,
  prerequisiteBadge,
  SIP_TRUNK_EXTENSION,
  SipExtensionInfo,
} from './outbound-readiness/outbound-readiness.util';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
import { countryFromCallingCode, isValidPhoneNumber, toE164 } from '../../core/validators/phone';
import {
  HlmAlertComponent,
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmTextareaDirective,
  HlmSelectComponent,
  HlmSwitchComponent,
  HlmSpinnerComponent,
  HlmTooltipDirective,
  HLM_FORM_FIELD,
  HlmSelectOption,
} from '../../ui';

interface WeekdayLabel {
  char: string;
  short: string;
}

interface GreetingSegment {
  isPlaceholder: boolean;
  value: string;
}

/** Splits a greeting into text and `[PLACEHOLDER]` segments for XSS-free rendering (no innerHTML). */
function toGreetingSegments(text: string): GreetingSegment[] {
  return text.split(/(\[[^\]]+\])/).filter(Boolean).map(part => ({
    isPlaceholder: part.startsWith('['),
    value: part,
  }));
}

interface SkillEditRow {
  skillName: string;
  nice: boolean;
  startTime: string;
  endTime: string;
  weekdays: boolean[];
}

@Component({
  selector: 'cmt-phone-service-info',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ErrorStateComponent,
    SipOrderLinkSectionComponent,
    HelpIconComponent,
    HlmIconComponent,
    HlmAlertComponent,
    HlmBadgeComponent,
    HlmBtnDirective,
    HlmInputDirective,
    HlmTextareaDirective,
    HlmSelectComponent,
    HlmSwitchComponent,
    HlmSpinnerComponent,
    HlmTooltipDirective,
    ...HLM_FORM_FIELD,
  ],
  templateUrl: './phone-service-info.component.html',
  // An inline host cannot shrink (min-width does not apply to inline elements), so its
  // widest child — the 810px skills track — would set the page width and defeat the
  // track's own scroll container (EB-4170 H7). The orphaned component SCSS used to
  // declare `:host { display: block }`, but nothing loads it.
  host: { class: 'block min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneServiceInfoComponent implements OnInit {
  private readonly api = inject(PhoneServiceApiService);
  private readonly userAccountApi = inject(UserAccountApiService);
  private readonly numbersApi = inject(CcNumbersService);
  private readonly contractsApi = inject(ContractsService);
  private readonly logging = inject(LoggingService);
  private readonly auth = inject(AuthService);
  readonly session = inject(SessionService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly phoneService = signal<PhoneServiceDetail | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly editMode = signal(false);
  readonly saving = signal(false);
  /** Account of this phone service, for the SIP order link block (needs its email). */
  readonly userAccount = signal<UserAccountDetail | null>(null);
  /** The account could not be loaded; the SIP order link block shows an error with Retry instead of a spinner. */
  readonly userAccountLoadFailed = signal(false);

  /** Verification state of the saved outbound number; null without a number or after a failed request. */
  readonly cliStatus = signal<CliSignalledNumber | null>(null);
  /** False until the first status answer (or failure) for the shown phone service; keeps the send button from flashing. */
  readonly cliStatusSettled = signal(false);
  /** Emits the phone service id, or null at load start to drop an answer for the previous one. */
  private readonly cliStatusRequests = new Subject<number | null>();

  /** Numbers of this phone service (for the SIP number); null while loading or after a failed request. */
  readonly phoneNumbers = signal<PhoneNumber[] | null>(null);
  /** The SIP-account extension in the contract; null while loading or after a failed request. */
  readonly sipExtension = signal<SipExtensionInfo | null>(null);
  /** False until numbers and contract answered (or failed) for the shown phone service. */
  readonly prerequisitesSettled = signal(false);
  /** Emits the loaded phone service, or null at load start to drop answers for the previous one. */
  private readonly prerequisiteRequests = new Subject<PhoneServiceDetail | null>();

  /**
   * Outbound prerequisites of this phone service (SIP account, own number, verification).
   *
   * @ticket PS-6598
   */
  readonly outboundReadiness = computed(() => {
    const ps = this.phoneService();
    return deriveOutboundReadiness({
      country: countryFromCallingCode(ps?.countryPrefix) ?? null,
      outboundNumber: ps?.outboundOutgoingNumber ?? null,
      cli: this.cliStatus(),
      extension: this.sipExtension(),
      numbers: this.phoneNumbers(),
    });
  });

  readonly currencySymbol = computed(() => getCurrency(this.phoneService()?.brandId ?? 1));

  /** Badges of the three prerequisite rows; null while their sources are still loading. */
  readonly numberBadge = computed(() => (this.cliStatusSettled() ? numberStateBadge(this.outboundReadiness()) : null));
  readonly extensionBadge = computed(() => (this.prerequisitesSettled() ? prerequisiteBadge(this.outboundReadiness().extension) : null));
  readonly sipNumberBadge = computed(() => (this.prerequisitesSettled() ? prerequisiteBadge(this.outboundReadiness().sipNumber) : null));

  /** Why the SIP order link cannot be sent right now; the send button is disabled with this text. */
  readonly sipOrderLinkBlocked = computed<string | null>(() => {
    if (this.editMode()) return 'Save or cancel your changes first';
    return this.outboundReadiness().sipAccountBlockedReason;
  });

  /**
   * The two positions the SIP order link books together (contract extension + SIP number), each
   * marked done or missing so the operator sees at a glance what the customer still lacks.
   */
  readonly sipOrderLinkBooks = computed<SipOrderItem[]>(() => {
    const r = this.outboundReadiness();
    const state = (s: string): SipOrderItem['state'] =>
      s === 'done' || s === 'notRequired' ? 'done' : s === 'unknown' ? 'unknown' : 'missing';
    return [
      { label: 'Contract extension', state: state(r.extension) },
      { label: 'SIP number', state: state(r.sipNumber) },
    ];
  });

  /** Why the link has no purpose here; the send button is hidden with this text. */
  readonly sipOrderLinkNotNeeded = computed<string | null>(() => {
    const r = this.outboundReadiness();
    if (!r.sipAccountNeeded) {
      return r.verificationMethod === 'GRANDFATHERED' ? 'the verified number needs no SIP account' : 'the SIP account is booked';
    }
    return null;
  });

  /** Waiting-queue rows; the help texts are the former inline explainers, moved behind the help icon. */
  readonly wqFields = [
    { key: 'priority', label: 'WQ Priority', help: 'Higher priority gets bonus waiting seconds. Empty = brand default, and without one it is derived from the called number.' },
    { key: 'ringTime', label: 'WQ Ring Time', help: 'Ring tone the caller hears before entering the queue. Empty = brand default; 0 there means no waiting queue.' },
    { key: 'timeout', label: 'WQ Timeout', help: 'Time waiting in the queue before the call is forwarded. Empty = redirector default (60s). Allowed 5–3600.' },
    { key: 'forwardTo', label: 'WQ Forward To', help: 'Number the call goes to after the timeout. Empty switches the forward off.' },
  ] as const;

  /** Opens the User Account page of this phone service, where the recipient email is maintained. */
  goToUserAccount(): void {
    const uaId = this.phoneService()?.userAccountId;
    if (!uaId) return;
    this.router.navigate(['ua', uaId, 'account-details'], { relativeTo: this.route.parent });
  }

  /** The waiting-queue rows in two columns: priority + ring time, timeout + forward. */
  readonly wqColumns = [this.wqFields.slice(0, 2), this.wqFields.slice(2)];


  // Reference data
  readonly languages = signal<Language[]>([]);
  readonly voicebotModes = signal<string[]>([]);
  readonly callGroups = signal<CallGroup[]>([]);

  // Skills
  readonly skills = signal<Skill[]>([]);
  readonly skillsLoading = signal(false);
  readonly addableSkills = signal<any[]>([]);

  // Skill filter for autocomplete
  readonly skillFilterText = signal('');
  /** Greeting text split into safe segments for XSS-free rendering (no innerHTML needed) */
  readonly greetingSegments = computed(() => toGreetingSegments(this.phoneService()?.greeting || '—'));

  /** Outbound greeting, split the same way as the greeting so both render alike. */
  readonly outboundGreetingSegments = computed(() => toGreetingSegments(this.phoneService()?.outboundGreeting ?? ''));

  /** Pre-computed chip CSS classes keyed by field name — avoids per-CD recalculation */
  readonly chipClasses = computed<Record<string, string>>(() => {
    const ps = this.phoneService();
    if (!ps) return {};
    const classify = (value: any): string => {
      if (value === null || value === undefined) return 'chip-neutral';
      if (typeof value === 'boolean') return value ? 'chip-on' : 'chip-off';
      return 'chip-neutral';
    };
    return {
      blockClip: classify(ps.blockClip),
      callerNumberVisible: classify(ps.callerNumberVisible),
      showAllEmailRecipients: classify(ps.showAllEmailRecipients),
      multipartSms: classify(ps.multipartSms),
      smsSenderName: classify(ps.smsSenderName),
      faxHandling: classify(ps.faxHandling),
      callRecordingAllowed: classify(ps.callRecordingAllowed),
      transcriptionAllowed: classify(ps.transcriptionAllowed),
      blindTransfer: classify(ps.blindTransfer),
      voiceBotMode: classify(ps.voiceBotMode),
      holidayAsSunday: classify(ps.holidayAsSunday),
    };
  });

  /** Badge variant for feature chip based on on/off/neutral state */
  readonly chipVariants = computed<Record<string, 'success' | 'destructive' | 'outline'>>(() => {
    const classes = this.chipClasses();
    const result: Record<string, 'success' | 'destructive' | 'outline'> = {};
    for (const [key, cls] of Object.entries(classes)) {
      result[key] = cls === 'chip-on' ? 'success' : cls === 'chip-off' ? 'destructive' : 'outline';
    }
    return result;
  });

  readonly filteredSkills = computed(() => {
    const all = this.addableSkills();
    const filter = this.skillFilterText().toLowerCase();
    if (!filter) return all;
    return all.filter(s => s.name.toLowerCase().includes(filter));
  });

  /** Select options for the skill picker dropdown */
  readonly addableSkillOptions = computed<HlmSelectOption<string>[]>(() =>
    this.addableSkills().map(s => ({ value: s.name, label: s.name })),
  );

  // Language options for select
  readonly languageOptions = computed<HlmSelectOption<number>[]>(() =>
    this.languages().map(l => ({ value: l.id, label: l.name })),
  );

  /**
   * True once `loadReferenceData()` has failed: the languages, voicebot modes and call
   * groups are all unknown, because forkJoin fails as a unit.
   *
   * Without this the guard in `save()` was bypassable through the very control it protects
   * (PR #42 re-review defect 2): the option lists unconditionally prepend "None", so with
   * an empty list that was the *only* choice on offer — and picking it takes the
   * `callGroupId === null` branch, which short-circuits the `?? ps.callGroup` fallback and
   * PUTs `callGroup: null`. Not silent, but the real value was neither shown nor
   * re-selectable, and the sole option was the destructive one.
   */
  readonly referenceDataFailed = signal(false);

  /**
   * Voicebot mode options. When the list is unknown, the record's own value is still
   * offered so the trigger shows the truth instead of a placeholder; the control is
   * disabled in that state, so nothing can be picked against a list we do not have.
   */
  readonly voicebotModeOptions = computed<HlmSelectOption<string | null>[]>(() => {
    const modes = this.voicebotModes();
    const current = this.phoneService()?.voiceBotMode ?? null;
    const known = modes.length ? modes : (current ? [current] : []);
    return [
      { value: null as any, label: 'None' },
      ...known.map(m => ({ value: m, label: m })),
    ];
  });

  /** Call group options — same rule as {@link voicebotModeOptions}. */
  readonly callGroupOptions = computed<HlmSelectOption<number | null>[]>(() => {
    const groups = this.callGroups();
    const current = this.phoneService()?.callGroup ?? null;
    const known = groups.length ? groups : (current ? [current] : []);
    return [
      { value: null as any, label: 'None' },
      ...known.map(g => ({ value: g.id, label: g.name })),
    ];
  });

  // Fax handling options
  readonly faxHandlingOptions: HlmSelectOption<string>[] = [
    { value: 'ACCEPT', label: 'Accept' },
    { value: 'REJECT', label: 'Reject' },
  ];

  /**
   * Three states, not a switch: `null` means the phone service inherits the brand default, and a
   * two-state control could never get back to it once someone had touched it.
   */
  readonly holidayAsSundayOptions: HlmSelectOption<string>[] = [
    { value: 'inherit', label: 'Brand default' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  // Blind transfer options
  readonly blindTransferOptions: HlmSelectOption<string>[] = [
    { value: 'HIDDEN', label: 'Hidden' },
    { value: 'VISIBLE', label: 'Visible' },
  ];

  // Skills editing state
  readonly addingSkill = signal(false);
  readonly editingSkillName = signal<string | null>(null);
  readonly skillSaving = signal(false);

  // Skill add/edit form state (not reactive form — simple signals)
  skillFormName = '';
  skillFormNice = false;
  /**
   * The API models the flag as `nice` (true = optional), but the column asks
   * "Required?" — so the switch is bound to the inverse: ON means "Must have".
   * `skillFormNice` stays the single source of truth that is sent to the backend.
   */
  get skillFormRequired(): boolean {
    return !this.skillFormNice;
  }
  set skillFormRequired(required: boolean) {
    this.skillFormNice = !required;
  }
  skillFormStartTime = '00:00:00';
  skillFormEndTime = '23:59:59';
  skillFormWeekdays: boolean[] = [true, true, true, true, true, true, true];

  readonly weekdayLabels: WeekdayLabel[] = [
    { char: 'M', short: 'Mo' },
    { char: 'D', short: 'Tu' },
    { char: 'M', short: 'We' },
    { char: 'D', short: 'Th' },
    { char: 'F', short: 'Fr' },
    { char: 'S', short: 'Sa' },
    { char: 'S', short: 'Su' },
  ];

  /** The weekday char pattern from the API: MDMDFSS */
  private readonly weekdayChars = ['M', 'D', 'M', 'D', 'F', 'S', 'S'];

  /**
   * Whether the Outbound section is visible: in edit mode, with outbound data, and always for a German
   * phone service - a missing own number is exactly what its prerequisites point out (PS-6598).
   */
  readonly showOutboundSection = computed(() => {
    const ps = this.phoneService();
    if (!ps) return false;
    if (this.editMode()) return true;
    return !!(ps.outboundOutgoingNumber || ps.outboundGreeting || ps.outgoingPhoneNumber)
      || this.outboundReadiness().applies;
  });

  /**
   * The Waiting Queue card is always shown, in view mode too: an empty queue reads as "—" per
   * field, which tells the operator more than a missing card (PS-6598 review). Before the split
   * it was hidden unless `waitingQueuePriority` was set, because it shared a card with outbound.
   */
  readonly showWaitingQueueSection = computed(() => this.phoneService() !== null);

  readonly form = new FormGroup({
    greeting: new FormControl('', { nonNullable: true }),
    admitOutsourcing: new FormControl(false, { nonNullable: true }),
    subAccountInfo: new FormControl('', { nonNullable: true }),
    countryPrefix: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^[0-9]+$/)],
    }),
    language: new FormControl(0, { nonNullable: true }),
    callnoteSubject: new FormControl('', { nonNullable: true }),
    callerNumberVisible: new FormControl(false, { nonNullable: true }),
    showAllEmailRecipients: new FormControl(false, { nonNullable: true }),
    blockClip: new FormControl(false, { nonNullable: true }),
    smsSenderName: new FormControl('', { nonNullable: true }),
    multipartSms: new FormControl(false, { nonNullable: true }),
    faxHandling: new FormControl('', { nonNullable: true }),
    blindTransfer: new FormControl('', { nonNullable: true }),
    callRecordingAllowed: new FormControl(false, { nonNullable: true }),
    transcriptionAllowed: new FormControl(false, { nonNullable: true }),
    voiceBotMode: new FormControl<string | null>(null),
    callGroupId: new FormControl<number | null>(null),
    holidayAsSunday: new FormControl('inherit', { nonNullable: true }),
    waitingQueuePriority: new FormControl<number | null>(null),
    waitingQueueRingTime: new FormControl(20, { nonNullable: true }),
    waitingQueueTimeout: new FormControl<number | null>(null),
    waitingQueueTimeoutForwardTo: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(30), c => this.phoneNumberValidator(c)],
    }),
    outboundOutgoingNumber: new FormControl<string | null>(null, { validators: [c => this.phoneNumberValidator(c)] }),
    outboundGreeting: new FormControl<string | null>(null),
    outgoingPhoneNumber: new FormControl<string | null>(null, { validators: [Validators.pattern(/^\S*$/)] }),
  });

  /**
   * Empty stays valid: for WQ Forward To an empty number is how the forward is switched off, and for the
   * outbound number it means "no override". Everything else has to be a real number in the phone service's
   * own country - the previous whitespace-only check accepted "0891234567", which then got dialled.
   */
  private phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value ?? '') as string;
    if (!value.trim()) {
      return null;
    }
    return isValidPhoneNumber(value, this.editedNumberCountry()) ? null : { phoneNumber: true };
  }

  /** ISO country the entered numbers are read in, taken from the phone service instead of a hardcoded "DE". */
  private editedNumberCountry(): string {
    return countryFromCallingCode(this.phoneService()?.countryPrefix) ?? 'DE';
  }

  ngOnInit(): void {
    this.cliStatusRequests.pipe(
      switchMap(psId => psId === null ? EMPTY : this.api.getSignalledNumber(psId).pipe(
        catchError(err => {
          this.logging.warn('cli_verification.status_load_failed', { phoneServiceId: psId, error: safeHttpMessage(err) });
          return of(null);
        }),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(status => {
      this.cliStatus.set(status);
      this.cliStatusSettled.set(true);
    });

    this.prerequisiteRequests.pipe(
      switchMap(ps => {
        if (!ps) return EMPTY;
        // Outside Germany the prerequisites read "not required" whatever the data says.
        if (!this.isGerman(ps)) return of({ numbers: null, extension: null });
        return forkJoin({
          numbers: this.numbersApi.getNumbers(ps.id).pipe(catchError(err => this.prerequisiteFailed('numbers', ps, err))),
          extension: this.loadSipExtension(ps).pipe(catchError(err => this.prerequisiteFailed('contract', ps, err))),
        });
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ numbers, extension }) => {
      this.phoneNumbers.set(numbers);
      this.sipExtension.set(extension);
      this.prerequisitesSettled.set(true);
    });

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Number(params['phoneServiceId']);
      if (id) {
        this.loadPhoneService(id);
        this.loadSkills(id);
      }
    });
  }

  /** Reload PS data using the last route param. Called from error-state retry. */
  reloadPhoneService(): void {
    const id = Number(this.route.snapshot.params['phoneServiceId']);
    if (id) {
      this.loadPhoneService(id);
      this.loadSkills(id);
    }
  }

  /** Id of the phone service being loaded; answers for any other id arrive late and are dropped. */
  private requestedPhoneServiceId: number | null = null;

  private loadPhoneService(psId: number): void {
    this.requestedPhoneServiceId = psId;
    this.loading.set(true);
    this.error.set(null);
    this.editMode.set(false);
    this.userAccount.set(null);
    this.userAccountLoadFailed.set(false);
    this.cliStatus.set(null);
    this.cliStatusSettled.set(false);
    this.cliStatusRequests.next(null);
    this.phoneNumbers.set(null);
    this.sipExtension.set(null);
    this.prerequisitesSettled.set(false);
    this.prerequisiteRequests.next(null);

    // Start reference data loading immediately using session brandId — no need to wait for getById
    const sessionBrandId = this.session.brandId();
    if (sessionBrandId) {
      this.loadReferenceData(sessionBrandId);
    }

    this.api.getById(psId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (ps) => {
        if (psId !== this.requestedPhoneServiceId) return;
        this.phoneService.set(ps);
        this.loading.set(false);
        // Outside Germany nothing on the page needs the verification state or the account.
        if (this.isGerman(ps)) {
          this.cliStatusRequests.next(ps.id);
          this.loadUserAccount(ps);
        }
        this.prerequisiteRequests.next(ps);
        // Reload reference data only if brand differs from session (edge case)
        if (!sessionBrandId || ps.brandId !== sessionBrandId) {
          this.loadReferenceData(ps.brandId);
        }
      },
      error: (err) => {
        if (psId !== this.requestedPhoneServiceId) return;
        this.error.set(extractErrorMessage(err, 'Failed to load phone service'));
        this.loading.set(false);
      },
    });
  }

  /**
   * Languages, voicebot modes and call groups for the edit form.
   *
   * `forkJoin` fails as a unit: one failing call leaves ALL THREE lists empty, so the
   * selects render placeholders over values the record actually holds. Silent before
   * (PR #42 review H2) — the operator had no way to know the form was showing less than
   * the truth. `save()` no longer loses the call group over it, but the failure still has
   * to be visible.
   */
  private loadReferenceData(brandId: number): void {
    forkJoin({
      languages: this.api.getLanguages(brandId),
      voicebotModes: this.api.getVoicebotModes(),
      callGroups: this.api.getCallGroups(),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.referenceDataFailed.set(false);
        this.languages.set(data.languages);
        this.voicebotModes.set(data.voicebotModes);
        this.callGroups.set(data.callGroups);
      },
      error: (err) => {
        this.referenceDataFailed.set(true);
        this.toast.open(
          extractErrorMessage(err, 'Failed to load reference data — languages, voicebot modes and call groups are unavailable'),
          'OK',
          SNACKBAR_ERROR_CONFIG,
        );
      },
    });
  }

  /**
   * The SIP-account extension of the phone service's contract: booked today, and whether (and for
   * what) the plan of today's revision offers it. Null (unknown) without a contract, a revision or a
   * plan - "not offered" is only claimed when the plan answered without the extension.
   */
  private loadSipExtension(ps: PhoneServiceDetail): Observable<SipExtensionInfo | null> {
    const contractId = Number(ps.contractId);
    if (!contractId) return of(null);
    const today = todayIso();
    const isSipTrunk = (name: string | undefined) => name === SIP_TRUNK_EXTENSION;
    return forkJoin({
      booked: this.contractsApi.getBookedExtensions(contractId, today),
      revision: this.contractsApi.getRevisionValidOn(contractId, today),
    }).pipe(
      switchMap(({ booked, revision }) => {
        const isBooked = booked.some(e => isSipTrunk(e.extensionPriceInfo?.name));
        const bookedOnly: SipExtensionInfo | null = isBooked ? { booked: true, monthlyFee: null } : null;
        if (!revision?.planId) return of(bookedOnly);
        return this.contractsApi.getExtensionsByPlan(revision.planId).pipe(
          map((offered): SipExtensionInfo => ({
            booked: isBooked,
            monthlyFee: offered.find(e => isSipTrunk(e.extensionPriceInfo?.name))?.extensionPriceInfo.monthlyFee ?? null,
          })),
          catchError(err => {
            this.prerequisiteFailed('plan', ps, err);
            return of(bookedOnly);
          }),
        );
      }),
    );
  }

  /** A failed prerequisite source reads as unknown; the rest of the page is unaffected. */
  private prerequisiteFailed(source: string, ps: PhoneServiceDetail, err: unknown): Observable<null> {
    this.logging.warn('outbound_readiness.load_failed', { source, phoneServiceId: ps.id, error: safeHttpMessage(err) });
    return of(null);
  }

  /** Whether the phone service is German; only there the own outbound number needs the SIP account. */
  private isGerman(ps: PhoneServiceDetail): boolean {
    return countryFromCallingCode(ps.countryPrefix) === 'DE';
  }

  /** Retry from the SIP order link block after the account failed to load. */
  retryUserAccount(): void {
    const ps = this.phoneService();
    if (ps) this.loadUserAccount(ps);
  }

  private loadUserAccount(ps: PhoneServiceDetail): void {
    this.userAccountLoadFailed.set(false);
    this.userAccountApi.getById(ps.userAccountId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      // a late answer for a phone service that is no longer shown must not land here
      next: ua => { if (this.phoneService()?.id === ps.id) this.userAccount.set(ua); },
      error: err => {
        if (this.phoneService()?.id !== ps.id) return;
        this.userAccountLoadFailed.set(true);
        this.logging.warn('sip_order_link.account_load_failed', {
          phoneServiceId: ps.id,
          userAccountId: ps.userAccountId,
          error: safeHttpMessage(err),
        });
      },
    });
  }

  private loadSkills(psId: number): void {
    this.skillsLoading.set(true);
    this.api.getSkills(psId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (skills) => {
        this.skills.set(skills);
        this.skillsLoading.set(false);
      },
      error: () => {
        this.skillsLoading.set(false);
      },
    });
  }

  private loadAddableSkills(): void {
    const ps = this.phoneService();
    if (!ps) return;
    this.api.getAddableSkills(ps.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (skills) => this.addableSkills.set(skills),
    });
  }

  /** Get language name by id */
  getLanguageName(langId: number): string {
    const lang = this.languages().find(l => l.id === langId);
    return lang?.name ?? String(langId);
  }

  /** Return CSS class for feature chip based on boolean/non-boolean value */
  getChipClass(value: any, field: string): string {
    if (value === null || value === undefined) return 'chip-neutral';
    if (typeof value === 'boolean') return value ? 'chip-on' : 'chip-off';
    return 'chip-neutral';
  }

  /** Display nullable boolean: true->Yes, false->No, null->Not set */
  boolDisplay(value: boolean | null | undefined): string {
    if (value === null || value === undefined) return 'Not set';
    return value ? 'Yes' : 'No';
  }

  // formatGreeting removed — replaced by greetingSegments computed signal (XSS-safe, no innerHTML)

  /** Format weekday pattern "MDMDFSS" to readable string */
  formatWeekdays(pattern: string): string {
    if (!pattern || pattern.length !== 7) return pattern || '—';

    const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const activeDays: string[] = [];

    for (let i = 0; i < 7; i++) {
      if (pattern[i] !== '_' && pattern[i] !== ' ') {
        activeDays.push(dayLabels[i]);
      }
    }

    if (activeDays.length === 7) return 'All days';
    if (activeDays.length === 5 && activeDays[4] === 'Fri') return 'Mon-Fri';
    if (activeDays.length === 0) return '—';
    return activeDays.join(', ');
  }

  /** Format time "HH:MM:SS" — show full HH:MM:SS */
  formatTime(time: string): string {
    if (!time) return '—';
    // Always show full HH:MM:SS for consistency with edit fields
    if (time.length === 5) return time + ':00';
    return time;
  }

  /** Convert weekday pattern string to boolean array */
  weekdayPatternToBooleans(pattern: string): boolean[] {
    if (!pattern || pattern.length !== 7) return [true, true, true, true, true, true, true];
    return pattern.split('').map(c => c !== '_' && c !== ' ');
  }

  /** Convert boolean array to weekday pattern string */
  weekdayBooleansToPattern(booleans: boolean[]): string {
    return booleans.map((active, i) => active ? this.weekdayChars[i] : '_').join('');
  }

  // --- PS Edit Mode ---

  startEdit(): void {
    const ps = this.phoneService();
    if (!ps) return;
    this.form.patchValue({
      greeting: ps.greeting,
      admitOutsourcing: ps.admitOutsourcing,
      subAccountInfo: ps.subAccountInfo,
      countryPrefix: ps.countryPrefix,
      language: ps.language,
      callnoteSubject: ps.callnoteSubject,
      callerNumberVisible: ps.callerNumberVisible,
      showAllEmailRecipients: ps.showAllEmailRecipients,
      blockClip: ps.blockClip,
      smsSenderName: ps.smsSenderName,
      multipartSms: ps.multipartSms,
      faxHandling: ps.faxHandling,
      blindTransfer: ps.blindTransfer,
      callRecordingAllowed: ps.callRecordingAllowed,
      transcriptionAllowed: ps.transcriptionAllowed,
      voiceBotMode: ps.voiceBotMode,
      callGroupId: ps.callGroup?.id ?? null,
      holidayAsSunday: ps.holidayAsSunday === null ? 'inherit' : String(ps.holidayAsSunday),
      waitingQueuePriority: ps.waitingQueuePriority,
      waitingQueueRingTime: ps.waitingQueueRingTime,
      waitingQueueTimeout: ps.waitingQueueTimeout,
      waitingQueueTimeoutForwardTo: ps.waitingQueueTimeoutForwardTo ?? '',
      outboundOutgoingNumber: ps.outboundOutgoingNumber,
      outboundGreeting: ps.outboundGreeting,
      outgoingPhoneNumber: ps.outgoingPhoneNumber,
    });
    this.editMode.set(true);
  }

  cancelEdit(): void {
    this.form.reset();
    this.editMode.set(false);
  }

  /** Cleared text field -> null, so the API keeps the field unset instead of storing an empty string */
  /**
   * Blank clears the field; anything else is stored in E.164. The form cannot be submitted with an invalid
   * number, so the undefined branch is unreachable in practice - falling back to the raw input keeps a future
   * bypass from silently dropping what the user typed.
   */
  private normalisedNumber(value: string | null): string | null {
    const blank = this.blankToNull(value);
    return blank === null ? null : (toE164(blank, this.editedNumberCountry()) ?? blank);
  }

  private blankToNull(value: string | null): string | null {
    const trimmed = (value ?? '').trim();
    return trimmed === '' ? null : trimmed;
  }

  save(): void {
    const ps = this.phoneService();
    if (!ps || this.form.invalid) return;

    this.saving.set(true);
    // `null` (the "None" option) and `0` are two different legitimate values here: call group 0 is
    // "Phoneservice - Standard", the most-used group of all. A truthiness check treats the pick as
    // empty and silently sends `callGroup: null`, so the operator's change never leaves the form.
    const callGroupId = this.form.controls.callGroupId.value;
    // A lookup miss is NOT "no call group". `callGroups()` is empty whenever
    // `loadReferenceData()` failed, while the control still holds the real id from
    // `startEdit()` — mapping that through a miss to `null` re-creates the exact data loss
    // this guard was written for (PR #42 review H2). Keep what the record already had.
    const selectedCallGroup = callGroupId === null
      ? null
      : this.callGroups().find(g => g.id === callGroupId) ?? ps.callGroup ?? null;

    const updated: PhoneServiceDetail = {
      ...ps,
      greeting: this.form.controls.greeting.value,
      admitOutsourcing: this.form.controls.admitOutsourcing.value,
      subAccountInfo: this.form.controls.subAccountInfo.value,
      countryPrefix: this.form.controls.countryPrefix.value,
      language: this.form.controls.language.value,
      callnoteSubject: this.form.controls.callnoteSubject.value,
      callerNumberVisible: this.form.controls.callerNumberVisible.value,
      showAllEmailRecipients: this.form.controls.showAllEmailRecipients.value,
      blockClip: this.form.controls.blockClip.value,
      smsSenderName: this.form.controls.smsSenderName.value,
      multipartSms: this.form.controls.multipartSms.value,
      faxHandling: this.form.controls.faxHandling.value,
      blindTransfer: this.form.controls.blindTransfer.value,
      callRecordingAllowed: this.form.controls.callRecordingAllowed.value,
      transcriptionAllowed: this.form.controls.transcriptionAllowed.value,
      voiceBotMode: this.form.controls.voiceBotMode.value,
      callGroup: selectedCallGroup,
      holidayAsSunday: this.form.controls.holidayAsSunday.value === 'inherit'
        ? null
        : this.form.controls.holidayAsSunday.value === 'true',
      waitingQueuePriority: this.form.controls.waitingQueuePriority.value,
      waitingQueueRingTime: this.form.controls.waitingQueueRingTime.value,
      waitingQueueTimeout: this.form.controls.waitingQueueTimeout.value,
      waitingQueueTimeoutForwardTo: this.normalisedNumber(this.form.controls.waitingQueueTimeoutForwardTo.value),
      outboundOutgoingNumber: this.normalisedNumber(this.form.controls.outboundOutgoingNumber.value),
      outboundGreeting: this.blankToNull(this.form.controls.outboundGreeting.value),
      outgoingPhoneNumber: this.blankToNull(this.form.controls.outgoingPhoneNumber.value),
    };

    this.api.save(updated).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.saving.set(false);
        this.toast.open('Phone service saved', 'OK', { duration: 3000 });
        // The operator may have moved on to another phone service while the save was in flight.
        if (Number(saved.id) !== this.requestedPhoneServiceId) return;
        const previous = this.phoneService();
        const current = { ...saved, id: Number(saved.id) };
        this.phoneService.set(current);
        this.editMode.set(false);
        if (!this.isGerman(current)) return;
        // A save changes no numbers and no contract; only the verification of the outbound number can change.
        this.cliStatusRequests.next(current.id);
        if (!previous || !this.isGerman(previous)) {
          // The country prefix moved the phone service into Germany: load what it was spared before.
          this.loadUserAccount(current);
          this.prerequisiteRequests.next(current);
        }
      },
      error: () => {
        this.saving.set(false);
        this.toast.open('Failed to save phone service', 'OK', SNACKBAR_ERROR_CONFIG);
      },
    });
  }

  // --- Skill CRUD ---

  startAddSkill(): void {
    this.loadAddableSkills();
    this.skillFormName = '';
    this.skillFilterText.set('');
    this.skillFormNice = false;
    this.skillFormStartTime = '00:00:00';
    this.skillFormEndTime = '23:59:59';
    this.skillFormWeekdays = [true, true, true, true, true, true, true];
    this.addingSkill.set(true);
    this.editingSkillName.set(null);
  }

  startEditSkill(skill: Skill): void {
    this.skillFormName = skill.skill.name;
    this.skillFormNice = skill.nice;
    this.skillFormStartTime = skill.startTime;
    this.skillFormEndTime = skill.endTime;
    this.skillFormWeekdays = this.weekdayPatternToBooleans(skill.weekdays);
    this.editingSkillName.set(skill.skill.name);
    this.addingSkill.set(false);
  }

  cancelSkillEdit(): void {
    this.addingSkill.set(false);
    this.editingSkillName.set(null);
  }

  onSkillFilterInput(value: string): void {
    this.skillFilterText.set(value);
  }

  onSkillSelected(skillName: string): void {
    this.skillFormName = skillName;
    this.skillFilterText.set(skillName);
  }

  toggleSkillWeekday(index: number): void {
    this.skillFormWeekdays = this.skillFormWeekdays.map((v, i) => i === index ? !v : v);
  }

  saveNewSkill(): void {
    const ps = this.phoneService();
    if (!ps || !this.skillFormName) return;

    this.skillSaving.set(true);
    const payload = {
      skill: { name: this.skillFormName },
      nice: this.skillFormNice,
      startTime: this.skillFormStartTime,
      endTime: this.skillFormEndTime,
      weekdays: this.weekdayBooleansToPattern(this.skillFormWeekdays),
    };

    this.auth.user$.pipe(
      take(1),
      switchMap(user => this.api.addSkill(ps.id, payload, user?.login ?? 'unknown')),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.addingSkill.set(false);
        this.skillSaving.set(false);
        this.loadSkills(ps.id);
        this.toast.open(`Skill "${this.skillFormName}" added`, 'OK', { duration: 3000 });
      },
      error: () => {
        this.skillSaving.set(false);
        this.toast.open('Failed to add skill', 'OK', SNACKBAR_ERROR_CONFIG);
      },
    });
  }

  saveEditedSkill(): void {
    const ps = this.phoneService();
    const editName = this.editingSkillName();
    if (!ps || !editName) return;

    this.skillSaving.set(true);
    const payload = {
      skill: { name: editName },
      nice: this.skillFormNice,
      startTime: this.skillFormStartTime,
      endTime: this.skillFormEndTime,
      weekdays: this.weekdayBooleansToPattern(this.skillFormWeekdays),
    };

    this.auth.user$.pipe(
      take(1),
      switchMap(user => this.api.updateSkill(ps.id, payload, user?.login ?? 'unknown')),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.editingSkillName.set(null);
        this.skillSaving.set(false);
        this.loadSkills(ps.id);
        this.toast.open(`Skill "${editName}" updated`, 'OK', { duration: 3000 });
      },
      error: () => {
        this.skillSaving.set(false);
        this.toast.open('Failed to update skill', 'OK', SNACKBAR_ERROR_CONFIG);
      },
    });
  }

  /** Go-to-Contract enabled only when current PS has a contractId. */
  readonly canGoToContract = computed(() => {
    const cid = this.phoneService()?.contractId;
    return cid !== undefined && cid !== null && String(cid).length > 0;
  });

  /**
   * Jump to the related contract from this PS. Read-only navigation —
   * the contracts page auto-expands/scrolls to the contractId (EB-3811).
   */
  goToContract(): void {
    const ps = this.phoneService();
    const contractId = ps?.contractId;
    if (contractId === undefined || contractId === null) return;
    this.router.navigate(['contracts'], {
      relativeTo: this.route.parent,
      queryParams: { contractId },
    });
  }

  /** Open the Call Viewer with this PS's customer + PS pre-filtered (EB-3811). */
  openCallViewer(): void {
    const ps = this.phoneService();
    const customerId = this.session.customer()?.id;
    if (!ps || customerId === undefined || customerId === null) {
      this.toast.open('Cannot open Call Viewer: missing scope', 'OK', { duration: 3000, tone: 'error' });
      return;
    }
    const link = buildCallViewerLink('ps', {
      customerId,
      phoneServiceId: ps.id,
    });
    this.router.navigate(link.commands, { queryParams: link.queryParams });
  }

  openCai(): void {
    const ps = this.phoneService();
    if (!ps) return;

    this.auth.user$.pipe(
      take(1),
      switchMap(user => {
        if (!user?.userId) return of(null);
        return this.api.getCaiLink(ps.id, String(user.userId));
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      if (data?.linkToCai?.startsWith('https://')) {
        window.open(data.linkToCai, '_blank', 'noopener,noreferrer');
      } else if (data?.linkToCai) {
        this.toast.open('Invalid CAI link', 'OK', { duration: 3000, tone: 'error' });
      } else {
        this.toast.open('No CAI link available', 'OK', { duration: 3000, tone: 'error' });
      }
    });
  }

  removeSkill(skillName: string): void {
    const ps = this.phoneService();
    if (!ps) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Skill',
        message: `Are you sure you want to remove the skill "${skillName}" from this phone service?`,
        confirmLabel: 'Remove',
        confirmColor: 'warn',
        icon: 'delete',
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.auth.user$.pipe(
        take(1),
        switchMap(user => this.api.removeSkill(ps.id, skillName, user?.login ?? 'unknown')),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe({
        next: () => {
          this.skills.update(current => current.filter(s => s.skill.name !== skillName));
          this.toast.open(`Skill "${skillName}" removed`, 'OK', { duration: 3000 });
        },
        error: () => {
          this.toast.open('Failed to remove skill', 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
    });
  }
}
